import type {AddonTier, OfferedAddon} from '../api/addons/types'

// Add-on price by quantity. This is the SAME rule the server uses to charge, written here only to
// SHOW a customer what a quantity costs while they choose. The amount a booking is charged is
// always the server's quote (`lineTotal`), never this number.
//
//   step  = the largest of [{1, unitPrice}, ...tiers] whose quantity is <= q
//   total = step.total + (q - step.quantity) * extraEach
//   extraEach = the add-on's own, else the last step's increase per item, else unitPrice
//   Example: unitPrice 300, tiers [{2,500},{3,650}], extraEach 150 -> 1:300 2:500 3:650 4:800

type PricedAddon = Pick<OfferedAddon, 'extraEach' | 'tiers' | 'unitPrice'>

export const roundMoney = (value: number): number => Math.round(Number(value) * 100) / 100

// Quantity 1 first, then the tiers in order of quantity.
export const addonPriceSteps = (addon: PricedAddon): AddonTier[] => {
  const tiers = (Array.isArray(addon.tiers) ? addon.tiers : [])
    .filter((tier) => Number.isInteger(tier?.quantity) && tier.quantity >= 2 && Number.isFinite(Number(tier.total)))
    .map((tier) => ({quantity: tier.quantity, total: roundMoney(tier.total)}))
    .sort((a, b) => a.quantity - b.quantity)
  return [{quantity: 1, total: roundMoney(addon.unitPrice || 0)}, ...tiers]
}

// What each one past the last step costs.
export const addonExtraEach = (addon: PricedAddon): number => {
  if (addon.extraEach !== null && addon.extraEach !== undefined && Number.isFinite(Number(addon.extraEach))) {
    return roundMoney(addon.extraEach)
  }
  const steps = addonPriceSteps(addon)
  if (steps.length < 2) {
    return roundMoney(addon.unitPrice || 0)
  }
  const last = steps[steps.length - 1]
  const before = steps[steps.length - 2]
  return roundMoney((last.total - before.total) / Math.max(1, last.quantity - before.quantity))
}

// The whole price of `quantity` items; 0 for no items; null when the quantity is not a whole number.
export const addonPriceForQuantity = (addon: PricedAddon, quantity: number): number | null => {
  if (!Number.isInteger(quantity) || quantity < 0) {
    return null
  }
  if (quantity === 0) {
    return 0
  }
  const steps = addonPriceSteps(addon)
  let step = steps[0]
  steps.forEach((candidate) => {
    if (candidate.quantity <= quantity) {
      step = candidate
    }
  })
  return roundMoney(step.total + (quantity - step.quantity) * addonExtraEach(addon))
}
